import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { IOfferingChange } from 'legacy/lut/components/widgets/public/partner360/offeringsSwithList/OfferingSwitcher'
import {
  changeProgramType,
  selectPartnerAccess,
} from 'legacy/lut/features/partner360/configuration'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { Nullable } from 'clickNgo/models/utils'
import { selectAppOwnerId } from 'core-components/sitebuilder/store/selectors/selectAppOwnerId'
import { toggleOffering } from 'partner360/helpers/offerings/toggleOffering'

import type { IFinancialCockpitData } from '../../api/types'
import {
  setCockpitOffering,
  setCockpitSwitchingOnOffering,
  updateCockpitOffering,
} from '../financialCockpitSlice'
import {
  selectCockpitCommitmentStatuses,
  selectCockpitOffering,
  selectEffectiveCockpitFYear,
} from '../selectors'
import { requestFinancialCockpitData } from '../thunks'

/* The server takes a moment to count a programme it has just switched on: the cockpit is fetched
   again until the programme comes back on, a few times at most. */
const SWITCH_ON_FETCH_ATTEMPTS = 5
const SWITCH_ON_RETRY_DELAY_MS = 1500

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms))

const isOfferingOn = (data: IFinancialCockpitData, partnerId: string, programType: string) =>
  data.PartnerOfferingsCatalog.some(
    (partner) =>
      partner.partnerId === partnerId.trim() &&
      partner.offerings.some((it) => it.programType === programType && it.enabled)
  )

/**
 * Turning one of the partner's offerings on or off, with the confirmation the switch asks for.
 * Every place in the cockpit that offers the switch drives it from here, so the request, the
 * dialog's answer and the reload that follows stay one flow.
 */
export const useCockpitOfferingSwitch = (partnerId: string) => {
  const dispatch = useAppDispatch()
  /* The year on the board: the one the switch is stored for and the cockpit fetched again for. */
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const commitmentStatuses = useSelector(selectCockpitCommitmentStatuses)
  const shownOffering = useSelector(selectCockpitOffering)
  const ownerId = useSelector(selectAppOwnerId)
  const { isDistributor } = useSelector(selectPartnerAccess)

  const [pendingChange, setPendingChange] = useState<Nullable<IOfferingChange>>(null)
  /** The offering the switch is being applied to; its control stays disabled until it lands. */
  const [loadingOffering, setLoadingOffering] = useState<Nullable<ProgramTypes>>(null)

  const applyChange = useCallback(
    async ({ offering, flag }: IOfferingChange) => {
      setLoadingOffering(offering.programType)

      /* A switched-off offering has no figures of its own, so turning it on fetches the cockpit
         again — under the board, which stays on screen and shows the switch in progress. */
      if (flag) {
        dispatch(setCockpitSwitchingOnOffering(offering.programType))
      }

      try {
        await toggleOffering({
          fYear,
          offering,
          flag,
          partnerId,
          dispatch,
          commitmentStatuses,
          isDistributor,
          ownerId,
        })
        dispatch(
          updateCockpitOffering({
            partnerId,
            offering: { ...offering, enabled: !offering.enabled },
          })
        )

        /* Turning an offering on selects it: it is the one the user just asked for. */
        if (flag) {
          await dispatch(changeProgramType(offering.programType))
          dispatch(setCockpitOffering(offering.programType))
          for (let attempt = 1; attempt <= SWITCH_ON_FETCH_ATTEMPTS; attempt += 1) {
            const data = await dispatch(
              requestFinancialCockpitData({
                fYear,
                partnerId,
                shouldResetCache: true,
                silent: true,
              })
            ).unwrap()

            if (
              isOfferingOn(data, partnerId, offering.programType) ||
              attempt === SWITCH_ON_FETCH_ATTEMPTS
            ) {
              break
            }

            await wait(SWITCH_ON_RETRY_DELAY_MS)
          }
        } else if (offering.programType === shownOffering) {
          /* The legacy configuration moves its selection off a programme switched off
             (`updateProgramTypeListener`) and the cockpit follows it. The reader switched this one
             off on purpose, so the cockpit stays on it, view only. Selected after the legacy move,
             so this choice is the one that lands last. */
          await dispatch(changeProgramType(offering.programType))
          dispatch(setCockpitOffering(offering.programType))
        }
      } finally {
        if (flag) {
          dispatch(setCockpitSwitchingOnOffering(null))
        }

        setLoadingOffering(null)
      }
    },
    [commitmentStatuses, dispatch, fYear, isDistributor, ownerId, partnerId, shownOffering]
  )

  const requestChange = useCallback((change: IOfferingChange) => {
    setPendingChange(change)
  }, [])

  const cancelChange = useCallback(() => {
    setPendingChange(null)
  }, [])

  const confirmChange = useCallback(() => {
    if (!pendingChange) {
      return
    }

    applyChange(pendingChange)
    setPendingChange(null)
  }, [applyChange, pendingChange])

  return {
    isDistributor,
    loadingOffering,
    pendingChange,
    applyChange,
    requestChange,
    confirmChange,
    cancelChange,
  }
}
